import { useCallback, useEffect, useMemo, useState } from 'react';
import { getApiErrorMessage } from '../services/errorUtils';

interface CacheState<T> {
  data: T[] | null;
  inFlight: Promise<T[]> | null;
}

interface UseCachedListValidationOptions<T> {
  /** Fetch function that returns raw items from the API. */
  fetchItems: () => Promise<T[]>;
  /** Normalize each item (e.g., uppercase + trim). */
  normalize: (item: T) => T;
  /** Human-readable label used in error messages (e.g., "SKUs", "shelves"). */
  label: string;
}

interface UseCachedListValidationResult<T> {
  items: T[];
  loading: boolean;
  error: string | null;
  validate: (item: T) => boolean;
  refresh: () => Promise<void>;
  invalidateCache: () => void;
}

/**
 * Generic hook: loads a list once, caches it at module level, deduplicates in-flight
 * requests, and provides a validation callback against the loaded set.
 *
 * Module-level caches are keyed by `label` to avoid collisions between hook instances.
 */
const caches = new Map<string, CacheState<unknown>>();

/** Clear module-level list cache so the next load refetches (e.g. after admin mutates shelves). */
export function invalidateCachedListByLabel(label: string): void {
  const entry = caches.get(label);
  if (entry) {
    entry.data = null;
    entry.inFlight = null;
  }
}

function getCache<T>(label: string): CacheState<T> {
  if (!caches.has(label)) {
    caches.set(label, { data: null, inFlight: null });
  }
  return caches.get(label) as CacheState<T>;
}

export function useCachedListValidation<T>(
  options: UseCachedListValidationOptions<T>,
): UseCachedListValidationResult<T> {
  const { fetchItems, normalize, label } = options;
  const cache = getCache<T>(label);

  const [items, setItems] = useState<T[]>(() => (cache.data as T[]) || []);
  const [loading, setLoading] = useState<boolean>(() => !cache.data);
  const [error, setError] = useState<string | null>(null);

  const loadItems = useCallback(
    async (forceRefresh = false): Promise<T[]> => {
      if (!forceRefresh && Array.isArray(cache.data)) {
        return cache.data;
      }
      if (!forceRefresh && cache.inFlight) {
        return cache.inFlight;
      }

      cache.inFlight = fetchItems()
        .then((raw: T[]) => {
          const normalized = (Array.isArray(raw) ? raw : [])
            .map(normalize)
            .filter(Boolean);
          const deduped = Array.from(new Set(normalized));
          cache.data = deduped;
          return deduped;
        })
        .finally(() => {
          cache.inFlight = null;
        });

      return cache.inFlight;
    },
    [cache, fetchItems, normalize],
  );

  useEffect(() => {
    let cancelled = false;
    setLoading(!cache.data);

    loadItems()
      .then((result) => {
        if (!cancelled) {
          setItems(result);
          setError(null);
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setError(getApiErrorMessage(err, `Failed to load ${label}`));
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const itemSet = useMemo(() => new Set(items.map(normalize)), [items, normalize]);

  const validate = useCallback(
    (item: T): boolean => {
      if (loading || items.length === 0) return false;
      return itemSet.has(normalize(item));
    },
    [loading, items, itemSet, normalize],
  );

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const result = await loadItems(true);
      setItems(result);
      setError(null);
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, `Failed to load ${label}`));
    } finally {
      setLoading(false);
    }
  }, [loadItems, label]);

  const invalidateCache = useCallback(() => {
    cache.data = null;
  }, [cache]);

  return { items, loading, error, validate, refresh, invalidateCache };
}
